import type { OffsetOptions } from "@floating-ui/react";
import type { PopoverPlacement } from "./types";

/** floating-ui 主轴 / 交叉轴偏移（相对 placement） */
export type PopoverAxisContentOffset = {
  mainAxis?: number;
  crossAxis?: number;
  alignmentAxis?: number | null;
};

/** 视口方向：上 / 右 / 下 / 左（与 placement 组合后映射到 floating-ui） */
export type PopoverInsetContentOffset = {
  top?: number;
  right?: number;
  bottom?: number;
  left?: number;
};

/**
 * 气泡与触发元素的间距。
 * - `number`：主轴间距（`bottom` 类 placement 即向下）
 * - `PopoverAxisContentOffset`：按 placement 主轴/交叉轴
 * - `PopoverInsetContentOffset`：按上下左右（更贴近设计标注）
 */
export type PopoverContentOffset =
  | number
  | PopoverAxisContentOffset
  | PopoverInsetContentOffset;

export const DEFAULT_POPOVER_CONTENT_OFFSET = 8;

function isAxisOffset(
  value: PopoverContentOffset,
): value is PopoverAxisContentOffset {
  return (
    typeof value === "object" &&
    ("mainAxis" in value || "crossAxis" in value || "alignmentAxis" in value)
  );
}

function isInsetOffset(
  value: PopoverContentOffset,
): value is PopoverInsetContentOffset {
  return (
    typeof value === "object" &&
    ("top" in value || "right" in value || "bottom" in value || "left" in value)
  );
}

function resolveInsetOffset(
  placement: PopoverPlacement,
  inset: PopoverInsetContentOffset,
): OffsetOptions {
  const top = inset.top ?? 0;
  const right = inset.right ?? 0;
  const bottom = inset.bottom ?? 0;
  const left = inset.left ?? 0;
  const [side, align] = placement.split("-") as [string, string | undefined];

  const hasExplicitMain =
    (side === "bottom" || side === "top") &&
    (inset.bottom !== undefined || inset.top !== undefined);
  const hasExplicitMainHorizontal =
    (side === "left" || side === "right") &&
    (inset.left !== undefined || inset.right !== undefined);

  switch (side) {
    case "bottom": {
      const mainAxis =
        inset.bottom ?? (hasExplicitMain ? 0 : DEFAULT_POPOVER_CONTENT_OFFSET);
      const crossAxis =
        align === "start"
          ? left - right
          : align === "end"
            ? right - left
            : (right - left) / 2;
      const options: OffsetOptions = { mainAxis, crossAxis };
      if (inset.top !== undefined && inset.bottom !== undefined) {
        options.alignmentAxis = top - bottom;
      }
      return options;
    }
    case "top": {
      const mainAxis =
        inset.top ?? (hasExplicitMain ? 0 : DEFAULT_POPOVER_CONTENT_OFFSET);
      const crossAxis =
        align === "start"
          ? left - right
          : align === "end"
            ? right - left
            : (right - left) / 2;
      const options: OffsetOptions = { mainAxis, crossAxis };
      if (inset.top !== undefined && inset.bottom !== undefined) {
        options.alignmentAxis = bottom - top;
      }
      return options;
    }
    case "right": {
      const mainAxis =
        inset.right ??
        (hasExplicitMainHorizontal ? 0 : DEFAULT_POPOVER_CONTENT_OFFSET);
      const crossAxis =
        align === "start"
          ? top - bottom
          : align === "end"
            ? bottom - top
            : (bottom - top) / 2;
      const options: OffsetOptions = { mainAxis, crossAxis };
      if (inset.left !== undefined && inset.right !== undefined) {
        options.alignmentAxis = left - right;
      }
      return options;
    }
    case "left": {
      const mainAxis =
        inset.left ??
        (hasExplicitMainHorizontal ? 0 : DEFAULT_POPOVER_CONTENT_OFFSET);
      const crossAxis =
        align === "start"
          ? top - bottom
          : align === "end"
            ? bottom - top
            : (bottom - top) / 2;
      const options: OffsetOptions = { mainAxis, crossAxis };
      if (inset.left !== undefined && inset.right !== undefined) {
        options.alignmentAxis = right - left;
      }
      return options;
    }
    default:
      return { mainAxis: DEFAULT_POPOVER_CONTENT_OFFSET };
  }
}

/** 将业务 offset 转为 floating-ui `offset()` 参数 */
export function resolvePopoverContentOffset(
  placement: PopoverPlacement,
  contentOffset: PopoverContentOffset = DEFAULT_POPOVER_CONTENT_OFFSET,
): number | OffsetOptions {
  if (typeof contentOffset === "number") {
    return contentOffset;
  }

  if (isAxisOffset(contentOffset)) {
    return {
      mainAxis: contentOffset.mainAxis ?? DEFAULT_POPOVER_CONTENT_OFFSET,
      crossAxis: contentOffset.crossAxis ?? 0,
      alignmentAxis: contentOffset.alignmentAxis ?? null,
    };
  }

  if (isInsetOffset(contentOffset) || Object.keys(contentOffset).length === 0) {
    return resolveInsetOffset(
      placement,
      contentOffset as PopoverInsetContentOffset,
    );
  }

  return DEFAULT_POPOVER_CONTENT_OFFSET;
}
